<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDebounceFn, useResizeObserver } from "@vueuse/core";
import type { PDFDocumentLoadingTask, PDFWorker } from "pdfjs-dist";
import type { EventBus, PDFLinkService, PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import { sleep } from "@bc-library/common";
import BButton from "@/components/common/bButton.vue";
import loading from "@/components/common/loading.vue";
import { loadPdfjs, type TPdfScale } from "@/components/common/pdf/pdfjs";
import PhArrowsOutLineHorizontal from "~icons/ph/arrows-out-line-horizontal";
import PhCaretDown from "~icons/ph/caret-down";
import PhCaretLeft from "~icons/ph/caret-left";
import PhCaretRight from "~icons/ph/caret-right";
import PhCaretUp from "~icons/ph/caret-up";
import PhDownloadSimple from "~icons/ph/download-simple";
import PhMagnifyingGlass from "~icons/ph/magnifying-glass";
import PhMagnifyingGlassMinus from "~icons/ph/magnifying-glass-minus";
import PhMagnifyingGlassPlus from "~icons/ph/magnifying-glass-plus";
import PhWarningCircle from "~icons/ph/warning-circle";
import "@/components/common/pdf/pdfViewer.css";

const props = withDefaults(
  defineProps<{
    /** The PDF bytes. pdf.js takes ownership of what it is given, so it gets a copy */
    data: Uint8Array | null;
    scale?: TPdfScale;
    /** Download name, with or without .pdf */
    fileName?: string;
    /** One page at a time, as slides */
    pageMode?: boolean;
  }>(),
  {
    scale: "page-width",
    fileName: "document",
    pageMode: false,
  },
);

const emits = defineEmits<{
  loaded: [pagesCount: number];
}>();

const { t } = useI18n();

const containerRef = useTemplateRef<HTMLDivElement>("container");
const pagesRef = useTemplateRef<HTMLDivElement>("pages");

const status = shallowRef<"loading" | "ready" | "error">("loading");
const pagesCount = shallowRef(0);
const pageNumber = shallowRef(1);
const scalePercent = shallowRef(100);
const query = shallowRef("");
const matches = shallowRef({ current: 0, total: 0 });

// pdf.js objects stay out of Vue's reactivity: behind a proxy their private fields throw
let pdfViewer: PDFViewer | null = null;
let eventBus: EventBus | null = null;
let linkService: PDFLinkService | null = null;
// One worker per viewer, terminated with it: no document can outlive the component
let worker: PDFWorker | null = null;
let loadingTask: PDFDocumentLoadingTask | null = null;
let loadCount = 0;
let isUnmounted = false;
const abortController = new AbortController();

const isReady = computed(() => status.value === "ready");

const setUpViewer = async () => {
  const { pdfjsLib, viewer } = await loadPdfjs();
  if (isUnmounted || !containerRef.value || !pagesRef.value) return null;

  eventBus = new viewer.EventBus();
  linkService = new viewer.PDFLinkService({
    eventBus,
    externalLinkTarget: viewer.LinkTarget.BLANK,
    externalLinkRel: "noopener noreferrer nofollow",
  });
  pdfViewer = new viewer.PDFViewer({
    container: containerRef.value,
    viewer: pagesRef.value,
    eventBus,
    linkService,
    findController: new viewer.PDFFindController({ eventBus, linkService }),
    // Links, no form filling
    annotationMode: pdfjsLib.AnnotationMode.ENABLE,
    removePageBorders: true,
    abortSignal: abortController.signal,
  });
  linkService.setViewer(pdfViewer);
  if (props.pageMode) pdfViewer.scrollMode = viewer.ScrollMode.PAGE;

  eventBus.on("pagesinit", applyScale);
  eventBus.on("pagechanging", ({ pageNumber: current }: { pageNumber: number }) => {
    pageNumber.value = current;
  });
  eventBus.on("scalechanging", ({ scale }: { scale: number }) => {
    scalePercent.value = Math.round(scale * 100);
  });
  const updateMatches = ({ matchesCount }: { matchesCount?: { current: number; total: number } }) => {
    if (matchesCount) matches.value = matchesCount;
  };
  eventBus.on("updatefindmatchescount", updateMatches);
  eventBus.on("updatefindcontrolstate", updateMatches);

  return pdfjsLib;
};

let viewerSetUp: ReturnType<typeof setUpViewer> | null = null;

const applyScale = () => {
  if (!pdfViewer?.pdfDocument) return;
  if (typeof props.scale === "number") pdfViewer.currentScale = props.scale;
  else pdfViewer.currentScaleValue = props.scale;
};

// The document leaves the worker with the viewer pages; the worker stays for the next one
const closeDocument = () => {
  const task = loadingTask;
  loadingTask = null;
  pdfViewer?.setDocument(null);
  linkService?.setDocument(null);
  pagesCount.value = 0;
  pageNumber.value = 1;
  query.value = "";
  matches.value = { current: 0, total: 0 };
  return task?.destroy();
};

const loadDocument = async (data: Uint8Array | null) => {
  const load = ++loadCount;
  status.value = "loading";
  void closeDocument();
  if (!data) return;

  try {
    const pdfjsLib = await viewerSetUp;
    if (!pdfjsLib || load !== loadCount) return;

    const verbosity = pdfjsLib.VerbosityLevel.ERRORS;
    worker ??= new pdfjsLib.PDFWorker({ verbosity });
    const task = pdfjsLib.getDocument({ data: data.slice(), worker, verbosity });
    loadingTask = task;
    const pdfDocument = await task.promise;
    // Another file or the unmount came meanwhile: they destroyed this task
    if (load !== loadCount || !pdfViewer) return;

    pdfViewer.setDocument(pdfDocument);
    linkService?.setDocument(pdfDocument, null);
    pagesCount.value = pdfDocument.numPages;
    status.value = "ready";
    emits("loaded", pdfDocument.numPages);
  } catch (error) {
    if (load !== loadCount) return;
    console.error("Failed to display PDF", error);
    status.value = "error";
  }
};

onMounted(() => {
  viewerSetUp = setUpViewer();
  loadDocument(props.data);
});

watch(() => props.data, loadDocument);
watch(() => props.scale, applyScale);

// A preset (page width, page fit) follows the room the viewer gets
const refitScale = useDebounceFn(() => {
  const value = pdfViewer?.currentScaleValue;
  if (pdfViewer && value && Number.isNaN(Number(value))) pdfViewer.currentScaleValue = value;
}, 100);
useResizeObserver(containerRef, refitScale);

onBeforeUnmount(() => {
  isUnmounted = true;
  loadCount++;
  const destroyed = closeDocument();
  abortController.abort();
  // Terminated once the document is destroyed in it, or anyway if it does not answer
  const terminate = () => worker?.destroy();
  if (destroyed) Promise.race([destroyed, sleep(2000)]).finally(terminate);
  else terminate();
});

const zoomIn = () => pdfViewer?.increaseScale();
const zoomOut = () => pdfViewer?.decreaseScale();
const fitWidth = () => {
  if (pdfViewer) pdfViewer.currentScaleValue = "page-width";
};

const previousPage = () => pdfViewer?.previousPage();
const nextPage = () => pdfViewer?.nextPage();
const goToPage = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const page = Math.min(Math.max(Math.round(Number(input.value)) || 1, 1), pagesCount.value || 1);
  input.value = String(page);
  if (pdfViewer) pdfViewer.currentPageNumber = page;
};

const find = (type: "" | "again", findPrevious = false) => {
  if (!query.value.trim()) {
    matches.value = { current: 0, total: 0 };
    eventBus?.dispatch("findbarclose", { source: null });
    return;
  }
  eventBus?.dispatch("find", {
    source: null,
    type,
    query: query.value,
    caseSensitive: false,
    entireWord: false,
    highlightAll: true,
    findPrevious,
    matchDiacritics: false,
  });
};
const findAsTyped = useDebounceFn(() => find(""), 250);

const matchLabel = computed(() =>
  matches.value.total
    ? t("components.pdfViewer.matchCount", matches.value)
    : t("components.pdfViewer.noMatch"),
);

const downloadName = computed(() => {
  const name = props.fileName.trim() || "document";
  return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
});

const download = async () => {
  const pdfDocument = pdfViewer?.pdfDocument;
  if (!pdfDocument) return;
  const url = URL.createObjectURL(new Blob([await pdfDocument.getData()], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = downloadName.value;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col overflow-hidden" data-cy="pdf-viewer">
    <div
      class="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-base bg-base px-2 py-1 text-xs"
      data-cy="pdf-viewer-toolbar"
    >
      <div class="flex items-center gap-1">
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady"
          :title="t('components.pdfViewer.zoomOut')"
          :aria-label="t('components.pdfViewer.zoomOut')"
          data-cy="pdf-viewer-zoom-out"
          @click="zoomOut"
        >
          <PhMagnifyingGlassMinus />
        </BButton>
        <span class="w-10 text-center tabular-nums" data-cy="pdf-viewer-zoom">{{ scalePercent }}%</span>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady"
          :title="t('components.pdfViewer.zoomIn')"
          :aria-label="t('components.pdfViewer.zoomIn')"
          data-cy="pdf-viewer-zoom-in"
          @click="zoomIn"
        >
          <PhMagnifyingGlassPlus />
        </BButton>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady"
          :title="t('components.pdfViewer.fitWidth')"
          :aria-label="t('components.pdfViewer.fitWidth')"
          data-cy="pdf-viewer-fit-width"
          @click="fitWidth"
        >
          <PhArrowsOutLineHorizontal />
        </BButton>
      </div>

      <div class="flex items-center gap-1">
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady || pageNumber <= 1"
          :title="t('components.pdfViewer.previousPage')"
          :aria-label="t('components.pdfViewer.previousPage')"
          data-cy="pdf-viewer-previous-page"
          @click="previousPage"
        >
          <PhCaretLeft />
        </BButton>
        <input
          :value="pageNumber"
          type="number"
          min="1"
          :max="pagesCount || 1"
          class="w-11 rounded-md border border-base bg-base px-1 py-0.5 text-center tabular-nums"
          :disabled="!isReady"
          :aria-label="t('components.pdfViewer.page')"
          data-cy="pdf-viewer-page"
          @change="goToPage"
        />
        <span class="subtitle whitespace-nowrap" data-cy="pdf-viewer-pages-count">
          {{ t("components.pdfViewer.pageCount", { count: pagesCount }) }}
        </span>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady || pageNumber >= pagesCount"
          :title="t('components.pdfViewer.nextPage')"
          :aria-label="t('components.pdfViewer.nextPage')"
          data-cy="pdf-viewer-next-page"
          @click="nextPage"
        >
          <PhCaretRight />
        </BButton>
      </div>

      <div class="ml-auto flex items-center gap-1">
        <div class="relative">
          <PhMagnifyingGlass
            class="subtitle pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          <input
            v-model="query"
            type="search"
            class="w-36 rounded-md border border-base bg-base py-0.5 pl-7 pr-2 md:w-52"
            :placeholder="t('components.pdfViewer.search')"
            :aria-label="t('components.pdfViewer.search')"
            :disabled="!isReady"
            data-cy="pdf-viewer-search"
            @input="findAsTyped"
            @keydown.enter.prevent="find('again', $event.shiftKey)"
          />
        </div>
        <span
          v-if="query.trim()"
          class="subtitle whitespace-nowrap tabular-nums"
          aria-live="polite"
          data-cy="pdf-viewer-matches"
        >
          {{ matchLabel }}
        </span>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!matches.total"
          :title="t('components.pdfViewer.previousMatch')"
          :aria-label="t('components.pdfViewer.previousMatch')"
          data-cy="pdf-viewer-previous-match"
          @click="find('again', true)"
        >
          <PhCaretUp />
        </BButton>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!matches.total"
          :title="t('components.pdfViewer.nextMatch')"
          :aria-label="t('components.pdfViewer.nextMatch')"
          data-cy="pdf-viewer-next-match"
          @click="find('again')"
        >
          <PhCaretDown />
        </BButton>
        <BButton
          size="dense"
          bg="base"
          icon
          :disabled="!isReady"
          :title="t('components.pdfViewer.download')"
          :aria-label="t('components.pdfViewer.download')"
          data-cy="pdf-viewer-download"
          @click="download"
        >
          <PhDownloadSimple />
        </BButton>
      </div>
    </div>

    <div class="relative min-h-0 flex-1 bg-fade">
      <div ref="container" class="absolute inset-0 overflow-auto" data-cy="pdf-viewer-pages">
        <div ref="pages" class="pdfViewer" />
      </div>
      <div v-if="status === 'loading'" class="absolute inset-0 flex items-center justify-center">
        <loading class="text-4xl" />
      </div>
      <div
        v-else-if="status === 'error'"
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center"
        data-cy="pdf-viewer-error"
      >
        <PhWarningCircle class="subtitle text-3xl" aria-hidden="true" />
        <p class="subtitle text-sm">{{ t("components.pdfViewer.loadError") }}</p>
      </div>
    </div>
  </div>
</template>
